import React, { useMemo, useState } from 'react';
import { Link } from 'react-navi';
import { Entry, EntryStage } from '../../types';
import classNames from 'classnames';
import { useStageInfo } from '../../hooks';

type StageSelection = {
  selection: { stage: EntryStage; label: string; href: string }[];
  current: { stage: EntryStage; label: string };
};

function getStageName(stage: EntryStage) {
  switch (stage) {
    case 3:
      return `二次審査`;
    case 4:
      return `三次審査`;
    case 5:
      return `四次審査`;
    case 6:
      return `セミファイナル`;
    case 7:
      return `ファイナル`;
    default:
      return '';
  }
}

export function EntryStageBreadCrumb(props: { stage: EntryStage; type: 'frecam' | 'misscircle' }) {
  const [isStageActive, setStageActive] = useState(false);
  const stageSelection: StageSelection = useMemo(() => {
    const current = { stage: props.stage, label: getStageName(props.stage) };
    switch (props.stage) {
      /**
       * ファイナル
       */
      case 7: {
        return {
          selection: [
            { stage: 6, label: getStageName(6), href: '/list/semifinal' },
            { stage: 5, label: getStageName(5), href: '/list/4' },
            { stage: 4, label: getStageName(4), href: '/list/3' },
            { stage: 3, label: getStageName(3), href: '/list/2' },
          ],
          current,
        };
      }
      /**
       * セミファイナル
       */
      case 6: {
        return {
          selection: [
            { stage: 7, label: getStageName(7), href: '/list/final' },
            { stage: 5, label: getStageName(5), href: '/list/4' },
            { stage: 4, label: getStageName(4), href: '/list/3' },
            { stage: 3, label: getStageName(3), href: '/list/2' },
          ],
          current,
        };
      }
      /**
       * 四次審査
       */
      case 5: {
        return {
          selection: [
            { stage: 7, label: getStageName(7), href: '/list/final' },
            { stage: 6, label: getStageName(6), href: '/list/semifinal' },
            { stage: 4, label: getStageName(4), href: '/list/3' },
            { stage: 3, label: getStageName(3), href: '/list/2' },
          ],
          current,
        };
      }
      /**
       * 三次審査
       */
      case 4: {
        return {
          selection: [
            { stage: 7, label: getStageName(7), href: '/list/final' },
            { stage: 6, label: getStageName(6), href: '/list/semifinal' },
            { stage: 5, label: getStageName(5), href: '/list/4' },
            { stage: 3, label: getStageName(3), href: '/list/2' },
          ],
          current,
        };
      }
      /**
       * 二次審査
       */
      case 3: {
        return {
          selection: [
            { stage: 7, label: getStageName(7), href: '/list/final' },
            { stage: 6, label: getStageName(6), href: '/list/semifinal' },
            { stage: 5, label: getStageName(5), href: '/list/4' },
            { stage: 4, label: getStageName(4), href: '/list/3' },
          ],
          current,
        };
      }
      default: {
        return {
          selection: [],
          current,
        };
      }
    }
  }, [props.stage]);

  return (
    <nav className="entry-breadcrumb is-margin-bottom-1">
      <div className="field is-grouped is-grouped-multiline is-align-items-center">
        <p className="control is-size-7">
          <a href={process.env.REACT_APP_LP_URL}>Top</a>
        </p>
        <p className="control is-size-7">/</p>
        <p className="control">
          <div
            className={classNames('dropdown', {
              'is-active': isStageActive,
            })}
          >
            <div className="dropdown-trigger">
              <button
                className="button is-rounded is-small"
                aria-haspopup="true"
                aria-controls="dropdown-menu2"
                onClick={() => toggleStageActive()}
              >
                <span>{stageSelection.current.label}</span>
                {stageSelection.selection.length > 0 && (
                  <span className="icon is-small">
                    <i className="fas fa-angle-down" aria-hidden="true" />
                  </span>
                )}
              </button>
            </div>
            {stageSelection.selection.length > 0 && (
              <div className="dropdown-menu" id="dropdown-menu2" role="menu" style={{ minWidth: '8rem' }}>
                <div className="dropdown-content">
                  {stageSelection.selection.map(({ stage, label, href }, index) => (
                    <Link className="dropdown-item" key={index} href={href} onClick={() => reset()}>
                      {label}
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </p>
      </div>
    </nav>
  );

  function toggleStageActive() {
    setStageActive(!isStageActive);
  }

  function reset() {
    setStageActive(false);
  }
}

export function ProfileBreadCrumb(props: { entry: Entry }) {
  const { entry } = props;
  const { stageName, stagePath } = useStageInfo(entry.stage);
  return (
    <nav className="entry-breadcrumb is-margin-bottom-1">
      <div className="field is-grouped is-grouped-multiline is-align-items-center">
        <p className="control is-size-7">
          <a href={process.env.REACT_APP_LP_URL}>Top</a>
        </p>
        <p className="control is-size-7">/</p>
        <p className="control is-size-7">
          <Link className="is-light" href={stagePath}>
            {stageName}
          </Link>
        </p>
        <p className="control is-size-7">/</p>
        <p className="control is-size-7">{entry.name}</p>
      </div>
    </nav>
  );
}
